/* eslint-disable no-throw-literal */
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import clubAPI from "../api/clubAPI";
import userAPI from "../api/userAPI";
import "../App.scss";
import MultipleEntryPassPaymentLabel from "../components/MultipleEntryPass/MultipleEntryPassPaymentLabel";
import swalDialog from "../helpers/swalErrorPopup";
import DeleteClientCreditCardPopup from "../shared/deleteCreditCardPopup";
import NotificationPopup from "../shared/notificationPopup";
import { PoweredBy } from "../shared/PoweredBy";
import SaveCardCreditPopup from "../shared/saveCardCreditPopup";
import { isLoadChanger } from "../store/mainSlice";
import {
  activeMultipleEntryPassesDetailsSelector,
  addNewMultipleEntryPass,
  canBuyNewMultipleEntryPassSelector,
  checkCanPurchasePass,
  fetchMultipleEntryPasses,
  hasActiveMultipleEntryPassSelector,
  selectIsRegularMaofEntryPossible,
} from "../store/multipleEntryPassSlice";
import { isPartnerClalit, selectIsMinor, selectIsOrthodox, selectPartner, isPartnerOvdimBari, isPartnerLeumit, isPartnerMaoflaamit } from "../store/partnerStore";
import { isV2Enabled, V2_SCREEN_KEYS } from "../v2/registry";
import { getPartnerAsset } from "../v2/theme/partnerAssets";
import V2ConfirmDialog from "../v2/components/ConfirmDialog";
import PurchaseSummaryV2 from "../v2/screens/purchaseSummary/PurchaseSummary";
import { updateAllAppParams } from "../store/appParamSlice";
import { updateIsHaveCreditCard } from "../store/userSlice";
import { removeWordIfExists } from "../utils/formatter";
import InfoIcon from "../img/svg/info_dark_blue.svg";

export const Summary = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const partner = useSelector(selectPartner);
  const isClalit = useSelector(isPartnerClalit);
  const isOvdimBari = useSelector(isPartnerOvdimBari);
  const isMaoflaamit = useSelector(isPartnerMaoflaamit);
  const isLeumit = useSelector(isPartnerLeumit);
  const isOrthodox = useSelector(selectIsOrthodox);

  const isMinor = useSelector(selectIsMinor);

  const hasActiveMultipleEntryPass = useSelector(hasActiveMultipleEntryPassSelector);

  const canBuyNewMultipleEntryPass = useSelector(canBuyNewMultipleEntryPassSelector);

  const multipleEntryPassDetails = useSelector(activeMultipleEntryPassesDetailsSelector);

  const isRegularMaofEntryPossible = useSelector(selectIsRegularMaofEntryPossible);

  const canBuyPass = !hasActiveMultipleEntryPass && canBuyNewMultipleEntryPass;

  const [openNotificationPopup, setOpenNotificationPopup] = useState(false);
  const [notificationText, setNotificationText] = useState("");
  const [selectedClub, setSelectedClub] = useState(null);
  const cityAndClubs = useSelector((state) => state.appParamSlice.allAppParams);
  const userParams = useSelector((state) => state.userSlice.userData);
  const mainUserParams = useSelector((state) => state.userSlice.mainUserData);
  const isHaveCard = useSelector((state) => state.userSlice.isHaveCreditCard);
  const [selectedMinutes, setSelectedMinutes] = useState({ minute: "00" });
  const location = useLocation();
  const [last4Digit, setLast4Disgit] = useState("");
  const [savedCreditCard, setSavedCreditCard] = useState(false);
  const [isSaveCreditCard, setIsSaveCreditCard] = useState(undefined);
  const [deleteCardCreditPopupOpen, setDeleteCartCreditPopupOpen] = useState(false);
  const [saveCreditCard, setSaveCreditCard] = useState(null);
  const [saveCardCreditPopupOpen, setSaveCartCreditPopupOpen] = useState(false);
  const [isCheckInClub, setIsCheckInClub] = useState(false);

  const [selectedDate, setSelectedDate] = useState({
    date: moment().format("DD/MM/YYYY"),
  });
  const [selectedHours, setSelectedHours] = useState({
    hour: moment().format("HH"),
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(canBuyPass ? "multipleEntryPass" : "creditCard");
  const [selectedPassPlanId, setSelectedPassPlanId] = useState(3);

  const paymentLabelRef = useRef();

  useEffect(() => {
    if (isMaoflaamit && isRegularMaofEntryPossible) {
      setSelectedPaymentMethod("creditCard");
    } else if (isMaoflaamit && !canBuyNewMultipleEntryPass && isRegularMaofEntryPossible === false) {
      setSelectedPaymentMethod("creditCard");
    }
  }, [isMaoflaamit, isRegularMaofEntryPossible, canBuyNewMultipleEntryPass]);

  // V2 design has no inline pass-payment selection — the method must stay creditCard so
  // calculatePrice() shows the club price and openIsCreditSaved() runs the card path only.
  useEffect(() => {
    if (isV2Enabled(V2_SCREEN_KEYS.SUMMARY, partner) && selectedPaymentMethod !== "creditCard") {
      setSelectedPaymentMethod("creditCard");
    }
  }, [partner, selectedPaymentMethod]);

  useEffect(() => {
    checkIsHasCreditCard();
    if (selectedClub) {
      setIsCheckInClub(
        !selectedClub.IsStudio &&
          !selectedClub.IsStudioBoostapp &&
          !selectedClub.IsStudioFizikal &&
          !selectedClub.IsStudioLeap &&
          !selectedClub.IsStudioRbox &&
          !selectedClub.IsStudioFeatShare
      );
    }
  }, [selectedClub]);

  const handlePayment = async () => {
    if (paymentLabelRef.current) {
      const result = await paymentLabelRef.current.handlePayment();
      // result is undefined when handlePayment early-returns (pass already active).
      if (result) {
        dispatch(addNewMultipleEntryPass(result));
        // The optimistic push above has the PurchaseFreeFitWebPass response shape;
        // reconcile with the GetAllFreefitWebPass shape the pass cards render from.
        dispatch(
          fetchMultipleEntryPasses({
            token: mainUserParams.Token,
            id: mainUserParams.ID,
            phone: mainUserParams.Phone,
            force: true,
          }),
        );
      }
      dispatch(
        checkCanPurchasePass({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        }),
      );
    }

    await fetchGetWebFullData();
  };

  const fetchGetWebFullData = async () => {
    if (isOvdimBari || isMaoflaamit) {
      try {
        const param = await clubAPI.getWebFullData({
          binID: userParams.BinID,
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        });
        if (param.data.Error !== 0) {
          throw { code: param.data.Error, mess: param.data.Message };
        }
        dispatch(updateAllAppParams(JSON.parse(param.data.Data)));
      } catch (err) {
        swalDialog(err);
      }
    }
  };

  const checkIsHasCreditCard = async () => {
    const chosenClub = cityAndClubs?.ClubList?.find((el) => el.RecordID == location?.state?.clubId);
    setSelectedClub(chosenClub);
    try {
      dispatch(isLoadChanger(true));

      let isHaveCreditCard = await userAPI.isClientHaveCreditCard({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      });

      if (isHaveCreditCard.data.Error != 0) {
        throw {
          code: isHaveCreditCard.data.Error,
          mess: isHaveCreditCard.data.Message,
        };
      }

      const jsonData = JSON.parse(isHaveCreditCard.data.Data);

      setSavedCreditCard(jsonData.IsCardSaved);

      dispatch(updateIsHaveCreditCard(jsonData.HasCreditCard));

      setLast4Disgit(jsonData.Last4Digit);

      dispatch(isLoadChanger(false));
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
    }
  };

  const formatDate = (originalDate) => {
    const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
    const [datePart, timePart] = originalDate?.split(" ");
    const [day, month, year] = datePart?.split(".");
    const [hours, minutes] = timePart?.split(":");
    const formattedDate = ` ${t("ON_DATE")} ${day}/${months[month - 1]}/${year.slice(-2)} ${t(
      "IN_A",
    )}${hours}:${minutes}`;
    return formattedDate;
  };

  const deleteCreditCardHandle = async () => {
    const result = await userAPI.deleteClientCreditCard({
      token: mainUserParams.Token,
      id: mainUserParams.ID,
      phone: mainUserParams.Phone,
    });

    if (result.data.Error != 0) {
      throw {
        code: result.data.Error,
        mess: result.data.Message,
      };
    }
  };

  const handleWebClubOrder = async (selectedClub, isSaveCreditCardLocal = false) => {
    try {
      dispatch(isLoadChanger(true));

      const orderParams = {
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
        cardNumber: userParams.CardNumber,
        source: isClalit ? null : (mainUserParams.Source ?? null),
        terminalID: selectedClub.TerminalID,
        binType: selectedClub.BinType,
        clubID: selectedClub.RecordID,
        isRbox:
          selectedClub.IsStudioRbox ||
          selectedClub.IsStudioFizikal ||
          selectedClub.IsStudioLeap ||
          selectedClub.IsStudioBoostapp ||
          selectedClub.IsStudioFeatShare,
      };

      if (location.state?.type === "book") {
        Object.assign(orderParams, {
          preOrderDate: location.state.selectedLesson.LessonStartDate,
          rboxLessonID: location.state.selectedLesson.RboxLessonID,
          lessonName: location.state.selectedLesson.LessonName,
          lessonStartDate: location.state.selectedLesson.LessonStartDate,
          lessonEndDate: location.state.selectedLesson.LessonEndDate,
          coachName: location.state.selectedLesson.CoachName,
          cancelationTime: location.state.selectedLesson.CancelationTime,
          isCancelAllow: location.state.selectedLesson.IsCancelAllow,
        });
      } else {
        Object.assign(orderParams, {
          preOrderDate: `${location.state.date.split("/").join(".")} ${location.state.hours}:${location.state.minutes}`,
          isOnlyCheckWithoutTransaction: false,
        });
      }

      const result = await clubAPI.createWebClubOrder(orderParams);

      if (result.data.Error != 0) {
        throw {
          code: result.data.Error,
          mess: result.data.Message,
        };
      }

      await fetchGetWebFullData();

      await userAPI.saveDeleteClientCreditCard({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
        saveCreditCard: isMinor ? false : isSaveCreditCardLocal,
      });

      dispatch(isLoadChanger(false));
      return result;
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
      return null;
    }
  };

  const redirectWithCondition = async (saveCreditCardDetails) => {
    dispatch(isLoadChanger(true));
    try {
      const selectedClub = cityAndClubs.ClubList.find((el) => el.RecordID === location.state.clubId);
      if (
        isHaveCard ||
        ((isClalit || isOvdimBari || isMaoflaamit) && hasActiveMultipleEntryPass && multipleEntryPassDetails.NumberOfEntries < 4)
      ) {
        if (location.state?.type === "checkin") {
          dispatch(isLoadChanger(true));

          if (savedCreditCard) {
            const result = await clubAPI.webClubEntry({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              cardNumber: userParams.CardNumber,
              source: isClalit ? null : (mainUserParams.Source ?? null),
              terminalID: selectedClub.TerminalID,
              clubID: selectedClub.RecordID,
              binType: selectedClub.BinType,
            });

            if (result.data.Error != 0) {
              throw {
                code: result.data.Error,
                mess: result.data.Message,
              };
            }

            await fetchGetWebFullData();

            await userAPI.saveDeleteClientCreditCard({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              saveCreditCard: isMinor ? false : saveCreditCardDetails,
            });

            dispatch(isLoadChanger(false));

            navigate("/orderConfirmation", {
              state: {
                date: moment().format("DD-MM-YYYY"),
                hours: moment().format("HH"),
                minutes: moment().format("mm"),
                clubId: selectedClub.RecordID,
                clubName: selectedClub.Name,
                nowDate: true,
                orderNumber: getOrderNumber(result),
                type: "checkin",
              },
            });
          } else if (savedCreditCard === false) {
            dispatch(isLoadChanger(true));
            const result = await clubAPI.webClubEntry({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              cardNumber: userParams.CardNumber,
              source: isClalit ? null : (mainUserParams.Source ?? null),
              terminalID: selectedClub.TerminalID,
              clubID: selectedClub.RecordID,
              binType: selectedClub.BinType,
            });

            if (result.data.Error != 0) {
              throw {
                code: result.data.Error,
                mess: result.data.Message,
              };
            }

            await fetchGetWebFullData();

            await userAPI.saveDeleteClientCreditCard({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              saveCreditCard: isMinor ? false : saveCreditCardDetails,
            });

            dispatch(isLoadChanger(false));

            navigate("/orderConfirmation", {
              state: {
                date: moment().format("DD-MM-YYYY"),
                hours: moment().format("HH"),
                minutes: moment().format("mm"),
                clubId: selectedClub.RecordID,
                clubName: selectedClub.Name,
                nowDate: true,
                orderNumber: getOrderNumber(result),
                type: "checkin",
              },
            });
          } else {
            dispatch(isLoadChanger(true));
            const result = await clubAPI.webClubEntry({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              cardNumber: userParams.CardNumber,
              source: isClalit ? null : (mainUserParams.Source ?? null),
              terminalID: selectedClub.TerminalID,
              clubID: selectedClub.RecordID,
              binType: selectedClub.BinType,
            });

            if (result.data.Error != 0) {
              throw {
                code: result.data.Error,
                mess: result.data.Message,
              };
            }

            await fetchGetWebFullData();

            await userAPI.saveDeleteClientCreditCard({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              saveCreditCard: isMinor ? false : saveCreditCardDetails,
            });

            dispatch(isLoadChanger(false));

            navigate("/orderConfirmation", {
              state: {
                date: moment().format("DD-MM-YYYY"),
                hours: moment().format("HH"),
                minutes: moment().format("mm"),
                clubId: selectedClub.RecordID,
                clubName: selectedClub.Name,
                nowDate: true,
                orderNumber: getOrderNumber(result),
                type: "checkin",
              },
            });
          }
        } else if (location.state?.type === "invite") {
          if (savedCreditCard) {
            dispatch(isLoadChanger(true));
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.date,
                  hours: location.state.hours,
                  minutes: location.state.minutes,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: true,
                  cancellationTime: cancellationTime,
                  orderNumber: getOrderNumber(succeedToOrder),
                  type: "invite",
                },
              });
            }
          } else if (savedCreditCard === false) {
            dispatch(isLoadChanger(true));
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));

              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.date,
                  hours: location.state.hours,
                  minutes: location.state.minutes,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: true,
                  cancellationTime: cancellationTime,
                  orderNumber: getOrderNumber(succeedToOrder),
                  type: "invite",
                },
              });
            }
          } else {
            dispatch(isLoadChanger(true));
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));

              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.date,
                  hours: location.state.hours,
                  minutes: location.state.minutes,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: true,
                  cancellationTime: cancellationTime,
                  orderNumber: getOrderNumber(succeedToOrder),
                  type: "invite",
                },
              });
            }
          }
        } else if (location.state?.type === "book") {
          const selectedClub = cityAndClubs.ClubList.find((el) => el.RecordID === location.state.clubId);
          dispatch(isLoadChanger(true));
          if (savedCreditCard) {
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.dateFull,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: false,
                  cancellationTime: cancellationTime,
                  lessonName: location.state?.selectedLesson?.LessonName,
                  lessonEndDate: location.state?.selectedLesson?.LessonEndDate,
                  orderNumber: getOrderNumber(succeedToOrder),
                  type: "book",
                },
              });
            }
          } else if (savedCreditCard === false) {
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.dateFull,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: false,
                  cancellationTime: cancellationTime,
                  lessonName: location.state?.selectedLesson?.LessonName,
                  lessonEndDate: location.state?.selectedLesson?.LessonEndDate,
                  orderNumber: getOrderNumber(succeedToOrder),
                  type: "book",
                },
              });
            }
          } else {
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.dateFull,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: false,
                  cancellationTime: cancellationTime,
                  lessonName: location.state?.selectedLesson?.LessonName,
                  lessonEndDate: location.state?.selectedLesson?.LessonEndDate,
                  orderNumber: getOrderNumber(succeedToOrder),
                  type: "book",
                },
              });
            }
          }
        }
      } else {
        navigate("/addCard", {
          state: {
            date: selectedDate.date,
            hours: selectedHours.hour,
            minutes: selectedMinutes.minute,
            clubId: location.state.clubId,
            clubName: location.state?.clubName,
            route: "orderConfirmation",
          },
        });
      }
      dispatch(isLoadChanger(false));
    } catch (err) {
      dispatch(isLoadChanger(false));
      setOpenNotificationPopup(true);
      setNotificationText(removeWordIfExists(err.mess, t("IMPORTANT")));
    }
    if (isClalit || isOvdimBari || isMaoflaamit) {
      dispatch(
        checkCanPurchasePass({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        }),
      );
      // An order paid with a pass consumes an entry — refetch so entries-left
      // reflects it (the once-per-session guard swallows non-forced dispatches).
      dispatch(
        fetchMultipleEntryPasses({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
          force: true,
        }),
      );
    }
  };

  const openIsCreditSaved = async () => {
    // Not saving credit card for "Minors" (MC-631)
    if (isMinor) {
      setIsSaveCreditCard(false);
      await buyNewMultipleEntryPass();
      redirectWithCondition(false);
    } else if (savedCreditCard && !isClalit && !isOvdimBari && !isMaoflaamit) {
      setIsSaveCreditCard(true);
      redirectWithCondition(true);
    } else {
      if (hasActiveMultipleEntryPass && (isClalit || isOvdimBari || isMaoflaamit)) {
        setIsSaveCreditCard(true);
        redirectWithCondition(true);
      } else {
        setDeleteCartCreditPopupOpen(true);
      }
    }
  };

  const buyNewMultipleEntryPass = async () => {
    if ((isClalit || isOvdimBari || isMaoflaamit) && selectedPaymentMethod === "multipleEntryPass" && !hasActiveMultipleEntryPass) {
      try {
        const result = await handlePayment();
      } catch (error) {
        return true;
      }
    } else return true;
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);
  };

  const calculatePrice = () => {
    if (selectedPaymentMethod === "multipleEntryPass" && isClalit) return 90;
    if (selectedPaymentMethod === "multipleEntryPass" && isMaoflaamit) return selectedPassPlanId === 3 ? 150 : 160;
    if (selectedPaymentMethod === "multipleEntryPass" && isOvdimBari) return 160;
    const club = selectedClub ?? cityAndClubs?.ClubList?.find((el) => el.RecordID == location?.state?.clubId);
    return club?.Price;
  };

  const getCancellationTime = (succeedToOrder) => {
    // Leap lessons don't echo CancellationTime in the order response — fall back to
    // the window carried on the selected lesson.
    const lessonFallback = Number(location.state?.selectedLesson?.CancelationTime) || 0;

    if (!succeedToOrder || !succeedToOrder.data || !succeedToOrder.data.Data) {
      return lessonFallback;
    }

    try {
      const jsonData = JSON.parse(succeedToOrder.data.Data);
      const cancellationTime = Number(jsonData?.[0]?.CancellationTime) || 0;
      return cancellationTime > 0 ? cancellationTime : lessonFallback;
    } catch (error) {
      return lessonFallback;
    }
  };

  // Data-only helper for the V2 confirmation screen: extracts the order id from the
  // order/entry API response. Field name varies — tries the plausible candidates and
  // returns null when absent (the V2 screen hides the order-number row then).
  const getOrderNumber = (response) => {
    if (!response?.data?.Data) return null;
    try {
      const jsonData = JSON.parse(response.data.Data);
      const order = Array.isArray(jsonData) ? jsonData[0] : jsonData;
      return order?.OrderID ?? order?.OrderId ?? order?.RecordID ?? order?.ID ?? null;
    } catch {
      return null;
    }
  };

  // V1's own usable-pass condition (NumberOfEntries = entries already used).
  const passActive = hasActiveMultipleEntryPass && (multipleEntryPassDetails?.NumberOfEntries ?? 4) < 4;
  const passRemainingEntries =
    (multipleEntryPassDetails?.PossibleNumberOfEntries ?? 0) - (multipleEntryPassDetails?.NumberOfEntries ?? 0);
  // Active pass fully committed (all entries held by future bookings) — booking is blocked,
  // even with a saved card.
  const passExhausted = hasActiveMultipleEntryPass && passRemainingEntries <= 0;

  // Shared by both the legacy markup and the V2 branch — openIsCreditSaved() depends on these popups.
  const dialogs = (
    <>
      <SaveCardCreditPopup
        isOpen={saveCardCreditPopupOpen}
        setIsOpen={setSaveCartCreditPopupOpen}
        cancelText={t("DO_NOT_SAVE")}
        confirmText={t("CONFIRM_GREAT")}
        headerText={t("SAVE_PAYMENT_METHOD")}
        bodyText={t("SAVE_CARD_MESSAGE")}
        // handleConfirm={() => setSaveCreditCard(true)}
        handleCancel={() => setSaveCreditCard(false)}
      />
      {isV2Enabled(V2_SCREEN_KEYS.SUMMARY, partner) ? (
        <V2ConfirmDialog
          isOpen={openNotificationPopup}
          onClose={() => setOpenNotificationPopup(false)}
          title={t("IMPORTANT")}
          body={notificationText || t("ERROR_POPUP_BODY")}
          primaryText={t("CLOSE")}
          onPrimary={() => setOpenNotificationPopup(false)}
        />
      ) : (
        <NotificationPopup
          isOpen={openNotificationPopup}
          setIsOpen={setOpenNotificationPopup}
          bodyTopText={t("IMPORTANT")}
          bodyText={notificationText}
          btnText={t("CLOSE")}
        />
      )}
      {isV2Enabled(V2_SCREEN_KEYS.SUMMARY, partner) ? (
        <V2ConfirmDialog
          isOpen={deleteCardCreditPopupOpen}
          onClose={() => setDeleteCartCreditPopupOpen(false)}
          title={t("SAVE_PAYMENT_METHOD")}
          body={t("SAVE_CARD_REUSE_MESSAGE")}
          primaryText={t("CONFIRM_GREAT")}
          onPrimary={async () => {
            setIsSaveCreditCard(true);
            setDeleteCartCreditPopupOpen(false);
            await buyNewMultipleEntryPass();
            redirectWithCondition(true);
          }}
          secondaryText={t("DO_NOT_SAVE")}
          onSecondary={async () => {
            setIsSaveCreditCard(false);
            setDeleteCartCreditPopupOpen(false);
            await buyNewMultipleEntryPass();
            redirectWithCondition(false);
          }}
        />
      ) : (
        <DeleteClientCreditCardPopup
          isOpen={deleteCardCreditPopupOpen}
          setIsOpen={setDeleteCartCreditPopupOpen}
          cancelText={t("CONFIRM_GREAT")}
          deleteText={t("DO_NOT_SAVE")}
          headerText={t("SAVE_PAYMENT_METHOD")}
          bodyText={t("SAVE_CARD_REUSE_MESSAGE")}
          handleDelete={async () => {
            setIsSaveCreditCard(false);
            setDeleteCartCreditPopupOpen(false);
            await buyNewMultipleEntryPass();
            redirectWithCondition(false);
          }}
          handleCancel={async () => {
            setIsSaveCreditCard(true);
            setDeleteCartCreditPopupOpen(false);
            await buyNewMultipleEntryPass();
            redirectWithCondition(true);
          }}
        />
      )}
    </>
  );

  if (isV2Enabled(V2_SCREEN_KEYS.SUMMARY, partner)) {
    return (
      <>
        <PurchaseSummaryV2
          type={location.state?.type}
          clubName={selectedClub?.Name ?? location.state?.clubName}
          lessonName={location.state?.selectedLesson?.LessonName}
          lessonStartDate={location.state?.selectedLesson?.LessonStartDate}
          lessonEndDate={location.state?.selectedLesson?.LessonEndDate}
          date={location.state?.date}
          hours={location.state?.hours}
          minutes={location.state?.minutes}
          address={selectedClub?.Address}
          isOrthodox={isOrthodox}
          imgSrc={selectedClub?.MobClubLogoPath}
          defaultImg={getPartnerAsset(partner, "defaultClubImg")}
          imgFallbackSrc={getPartnerAsset(partner, "clubFallbackImg")}
          hasCard={isHaveCard}
          last4Digit={last4Digit}
          total={calculatePrice()}
          passActive={passActive}
          passRemainingEntries={passRemainingEntries}
          passExhausted={passExhausted}
          showPassBanner={canBuyPass && isClalit}
          onPay={openIsCreditSaved}
          onPaymentRowClick={() => {
            if (isHaveCard) {
              navigate("/card-details");
            } else {
              navigate("/addCard", {
                state: {
                  date: selectedDate.date,
                  hours: selectedHours.hour,
                  minutes: selectedMinutes.minute,
                  clubId: location.state.clubId,
                  clubName: location.state?.clubName,
                  route: "orderConfirmation",
                },
              });
            }
          }}
          onPassBannerClick={() => navigate("/discover-pass-card")}
        />
        {dialogs}
      </>
    );
  }

  return (
    <div className="main-page-container">
      <div className={(isClalit || isOvdimBari || isMaoflaamit || isLeumit) ? "fit-selector-with-background" : "fit-selector-with-background-wow"}>
        <div className="main-page-btn-container">
          <div>
            <div className={`bottom-popup`}>
              <div className="popup-content">
                <div
                  className={isMaoflaamit ? "popup-content-wrapper-maof" : "popup-content-wrapper"}
                  style={
                    hasActiveMultipleEntryPass && multipleEntryPassDetails.NumberOfEntries < 4 ? { flexGrow: 0.3 } : {}
                  }
                >
                  <div>
                    <div className="mobile-popup-header">
                      <p className="mobile-popup-header-text">{t("PURCHASE_SUMMARY")}</p>
                    </div>
                    <div
                      className={isClalit || isOvdimBari || isMaoflaamit ? "mobile-popup-header-info" : "mobile-popup-header-info-wow"}
                    >
                      <p style={{ direction: "rtl" }}>{selectedClub?.Name}</p>

                      {location.state?.type === "checkin" ? (
                        ""
                      ) : location.state?.type === "book" && location.state?.dateFull ? (
                        formatDate(location.state?.dateFull)
                      ) : (
                        <>
                          {t("ON_DATE")} {location.state.date} {t("IN_A")}
                          {location.state.hours}:{location.state.minutes}
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    className="mobile-popup-content-wrapper"
                    style={
                      isMaoflaamit && !hasActiveMultipleEntryPass && canBuyNewMultipleEntryPass && !isRegularMaofEntryPossible
                        ? { paddingBottom: "8px" }
                        : {}
                    }
                  >
                    <div
                      className="mobile-popup-text-content-container"
                      style={
                        isMaoflaamit && !hasActiveMultipleEntryPass && canBuyNewMultipleEntryPass && !isRegularMaofEntryPossible
                          ? { gap: "8px" }
                          : {}
                      }
                    >
                      <MultipleEntryPassPaymentLabel
                        ref={paymentLabelRef}
                        last4Digit={last4Digit}
                        selectedPaymentMethod={selectedPaymentMethod}
                        onPaymentMethodChange={handlePaymentMethodChange}
                        onPassPlanChange={setSelectedPassPlanId}
                      />
                      {(!hasActiveMultipleEntryPass ||
                        (hasActiveMultipleEntryPass &&
                          (multipleEntryPassDetails === undefined ||
                            multipleEntryPassDetails.NumberOfEntries >= 4))) && (
                        <div
                          className={
                            isClalit || isOvdimBari || isMaoflaamit ? "mobile-popup-text-footer-purchase" : "mobile-popup-text-footer"
                          }
                        >
                          <p className="mobile-popup-text-thin">{t("TOTAL_PRICE")}:</p>
                          {calculatePrice() > 0 && <p>₪{calculatePrice()}</p>}
                        </div>
                      )}
                      {!isClalit && !isOvdimBari && !isMaoflaamit && isCheckInClub && (
                        <div className="summary-checkin-club-message">
                          <img src={InfoIcon} alt="info" />
                          {t("SUMMARY_CHECKIN_CLUB_CANCEL_MESSAGE")}
                        </div>
                      )}
                      {(isClalit || isOvdimBari) && !hasActiveMultipleEntryPass && (
                        <span className="payment-method-description">
                          {selectedPaymentMethod === "multipleEntryPass" && (
                            <>{t("PAYMENT_MULTIPLE_ENTRY_PASS_DESCRIPTION")}</>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mobile-popup-btn-block">
                  <button
                    style={{ fontSize: "18px", fontWeight: 700, width: "100%" }}
                    className={
                      isClalit || isOvdimBari || isMaoflaamit
                        ? "main-page-btn big-button very-big-button lightred-btn"
                        : "payment-button-wow"
                    }
                    onClick={() => openIsCreditSaved()}
                  >
                    {t("PAY")}
                  </button>
                  {isClalit && <PoweredBy />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {dialogs}
    </div>
  );
};
